import { Box, Grid } from '@mui/material'
import { type ReactElement } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl, type MessageDescriptor } from 'react-intl'
import { useAppSelector } from '../../app/hooks'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import { useCreateLocationMutation, useUpdateLocationMutation } from '../../app/redux-fetch/apiCoreQuery'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender'
import DefaultSpacingGrid from '../../components/Containers/DefaultSpacingGrid'
import { ContactCategoryTypeId, LocationType } from '../../types/common'
import { useSideModalContext } from '../Assets/modals/SideModalContext'
import ContactTable from '../Contacts/ContactTable'
import { ILocationByTenantCustomer } from '../Shipments/types'
import { fetchLoggedInUserSelector } from '../selectors'
import AddressDetails from './components/AddressDetails'
import DefaultValues from './components/DefaultValues'
import MainInfo from './components/MainInfo'
import messages from './messages'
import { getLocationResolver } from './validation'

export interface ILocationForm {
  location?: ILocationByTenantCustomer
  isDisabled?: boolean
  isCustomerDisabled?: boolean
  locationTypeId?: number
  isSelectLocationType?: boolean
  onSubmit?: (data: any) => void
}

const LocationForm = ({ location, isCustomerDisabled, isDisabled, locationTypeId, isSelectLocationType, onSubmit }: ILocationForm): ReactElement<any, any> => {

  const isNew = !location?.id

  const canBeParented = !locationTypeId || locationTypeId === LocationType.Terminal || locationTypeId === LocationType.FuelStation

  const { formatMessage } = useIntl()

  const { onClose } = useSideModalContext()

  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data)

  const customerId = location?.customerId ?? loggedInUser?.customerId

  const tenantId = loggedInUser?.tenantId

  const [createLocation] = useCreateLocationMutation()
  const [updateLocation] = useUpdateLocationMutation()

  const isTenantUser = loggedInUser?.tenantId && !loggedInUser?.customerId

  const { enqueueSuccess, enqueueFailure } = useNotificationStack()

  const methods = useForm<any>({
    resolver: getLocationResolver(formatMessage),
    defaultValues: {
      ...location,
      tenantId,
      customerId,
      locationTypeId: location?.locationTypeId ? location?.locationTypeId : locationTypeId ? locationTypeId : 255,
      systemDefined: location?.systemDefined ?? false,
      enabled: location?.enabled ?? true,
    },
  })

  const getCreatedMessage = (typeId?: number): MessageDescriptor => {
    switch (typeId) {
      case LocationType.Shipper:
        return messages.shipperCreated
      case LocationType.Airport:
        return messages.airportCreated
      case LocationType.Seaport:
        return messages.seaportCreated
      case LocationType.Terminal:
        return messages.terminalCreated
      case LocationType.FuelStation:
        return messages.fuelStationCreated
      case LocationType.Standard:
        return messages.standardLocationCreated
      default:
        return messages.locationCreated
    }
  }

  const getUpdatedMessage = (typeId?: number): MessageDescriptor => {
    switch (typeId) {
      case LocationType.Shipper:
        return messages.shipperUpdated
      case LocationType.Airport:
        return messages.airportUpdated
      case LocationType.Seaport:
        return messages.seaportUpdated
      case LocationType.Terminal:
        return messages.terminalUpdated
      case LocationType.FuelStation:
        return messages.fuelStationUpdated
      case LocationType.Standard:
        return messages.standardLocationUpdated
      default:
        return messages.locationUpdated
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      if (isNew) {
        values.locationTypeId = locationTypeId ? locationTypeId : values.locationTypeId ? values.locationTypeId : 255
        const data = await createLocation({ tenantId, customerId: values.customerId, body: values }).unwrap()

        enqueueSuccess(getCreatedMessage(locationTypeId ?? values.locationTypeId))
        onSubmit && onSubmit(data)
      } else {
        await updateLocation({ tenantId, customerId: values.customerId, body: values }).unwrap()
        enqueueSuccess(getUpdatedMessage(locationTypeId ?? values.locationTypeId))
      }
      onClose()
    } catch (error) {
      if (isNew) {
        enqueueFailure(messages.locationCreateError)
      } else {
        enqueueFailure(messages.locationUpdateError)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <Box>
        <DefaultSpacingGrid container sx={{ p: '48px' }}>
          <Grid display='flex' flexDirection='row' gap={4} width='100%'>
            <Grid display='flex' flexDirection='column' width='50%'>
              <MainInfo
                type={isNew ? 'create' : 'edit'}
                isTenantUser={Boolean(isTenantUser)}
                isDisabled={isDisabled}
                isCustomerDisabled={isCustomerDisabled}
                isOrderLocation={location?.isOrderLocation}
                locationTypeId={locationTypeId ?? 255}
                isSelectLocationType={isSelectLocationType}
                canBeParented={canBeParented}
              />
              <ConditionalRender condition={locationTypeId !== LocationType.Shipper}>
                <DefaultValues isDisabled={isDisabled} />
                <ContactTable isDisabled={isDisabled} contactCategoryId={ContactCategoryTypeId.location} />
              </ConditionalRender>
            </Grid>
            <Grid display='flex' flexDirection='column' width='50%'>
              <Grid item xs={12}>
                <AddressDetails isDisabled={isDisabled} />
              </Grid>
            </Grid>
          </Grid>
        </DefaultSpacingGrid>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            zIndex: 999,
          }}
        >
          {!isDisabled && <SubmitButtons create={isNew} isEnabled={true} onCancel={onClose} submitForm={methods.handleSubmit(handleSubmit)} />}
        </Box>
      </Box>
    </FormProvider>
  )
}

export default LocationForm
